import React from 'react';

/**
 * MedardOwlEngraving — the owner's engraved delft-line Médard (fine navy
 * line, full body, ear tufts, delft filigree, « LM » shield pendant) as a
 * transparent PNG, cut from the original ivory plate so it composites on
 * aman-parchment and aman-ivory alike.
 *
 * This artwork replaces the hand-coded stroke owls wherever Médard appears
 * as a VISIBLE brand mark (colophons, the éveil ceremony, margin watermarks,
 * empty states). Only sub-24px inline glyphs keep the SVG poses — at those
 * sizes the filigree would turn to noise.
 *
 * Always decorative: alt="" + aria-hidden, never draggable, never
 * selectable. Opacity is the caller's affair (full at colophons, CSS
 * opacity for quiet watermarks).
 *
 * Props:
 *   className — sizing utilities (give a HEIGHT; width follows the plate's
 *               ~0.59 aspect ratio via w-auto)
 *   size      — 'full' (480px source) | 'small' (160px source, ≤ ~40px marks)
 *   tone      — 'delft' (navy ink, for ivory/parchment plates) | 'ivory'
 *               (white ghost recolor #F4EFE7, for manoir-delft/navy plates
 *               where the delft ink is invisible — 1.03:1)
 */
export const MEDARD_ENGRAVING_SRC = '/medard/medard-owl-engraved.png';
export const MEDARD_ENGRAVING_SRC_SMALL = '/medard/medard-owl-engraved-160.png';
// ⚑ THE IVORY PLATE IS SERVED FROM ITS RENDITIONS (audit 10/09 bis, perf n°5,
// rest). The original `medard-owl-engraved-ivory.png` is 285×480 RGBA for 24
// colours — 90 898 bytes where a palette holds the SAME pixels in 36 Ko
// (tools/images/decors.py, `exact`: proved pixel for pixel, never assumed).
// The 285 px file is the 2× rendition of the 148 px slot on the home's navy
// plate; the 148 px one serves 1× screens. The original stays on disk as the
// tool's source and is never named by a component.
export const MEDARD_ENGRAVING_SRC_IVORY = '/medard/medard-owl-engraved-ivory-285.png';
export const MEDARD_ENGRAVING_SRC_IVORY_1X = '/medard/medard-owl-engraved-ivory-148.png';
export const MEDARD_ENGRAVING_IVORY_SRCSET = `${MEDARD_ENGRAVING_SRC_IVORY_1X} 148w, ${MEDARD_ENGRAVING_SRC_IVORY} 285w`;
// The plates' own pixels — the full (delft and ivory, 285×480) and the small
// (95×160) — for callers that reserve the slot with width/height before the
// PNG lands (audit 10/09/2026, CLS). A test reads them back from the files.
export const MEDARD_ENGRAVING_WIDTH = 285;
export const MEDARD_ENGRAVING_HEIGHT = 480;
export const MEDARD_ENGRAVING_SMALL_WIDTH = 95;
export const MEDARD_ENGRAVING_SMALL_HEIGHT = 160;

export default function MedardOwlEngraving({
    className = '',
    size = 'full',
    tone = 'delft',
    loading = 'lazy',
    'data-testid': testId,
}) {
    const src = tone === 'ivory'
        ? MEDARD_ENGRAVING_SRC_IVORY
        : size === 'small' ? MEDARD_ENGRAVING_SRC_SMALL : MEDARD_ENGRAVING_SRC;
    return (
        <img
            src={src}
            alt=""
            aria-hidden="true"
            draggable={false}
            loading={loading}
            data-testid={testId}
            className={`w-auto select-none pointer-events-none ${className}`}
        />
    );
}
